import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Images, Plus, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const AdminDashboard = () => {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-article-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('is_published');

      if (error) throw error;

      const published = data?.filter(a => a.is_published).length || 0;
      const drafts = data?.filter(a => !a.is_published).length || 0;

      return { published, drafts, total: data?.length || 0 };
    },
  });

  const { data: recentArticles, isLoading: articlesLoading } = useQuery({
    queryKey: ['admin-recent-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, title, slug, is_published, updated_at')
        .order('updated_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Vue d'ensemble de votre contenu</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" asChild>
            <Link to="/admin/galeries">
              <Images className="mr-2 h-4 w-4" />
              Gérer les galeries
            </Link>
          </Button>
          <Button asChild>
            <Link to="/admin/articles/new">
              <Plus className="mr-2 h-4 w-4" />
              Nouvel article
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Galeries photo</CardTitle>
            <CardDescription>Créer les galeries d’événements, gérer les mots de passe et téléverser les photos.</CardDescription>
          </div>
          <Button asChild>
            <Link to="/admin/galeries">
              <Images className="mr-2 h-4 w-4" />
              Ouvrir
            </Link>
          </Button>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Articles</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats?.total}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Publiés</CardTitle>
            <Eye className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {statsLoading ? '...' : stats?.published}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Brouillons</CardTitle>
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">
              {statsLoading ? '...' : stats?.drafts}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Articles */}
      <Card>
        <CardHeader>
          <CardTitle>Articles récents</CardTitle>
          <CardDescription>Les 5 derniers articles modifiés</CardDescription>
        </CardHeader>
        <CardContent>
          {articlesLoading ? (
            <p className="text-muted-foreground">Chargement...</p>
          ) : recentArticles?.length === 0 ? (
            <p className="text-muted-foreground">Aucun article</p>
          ) : (
            <div className="space-y-4">
              {recentArticles?.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex-1">
                    <Link
                      to={`/admin/articles/${article.slug}/edit`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {article.title}
                    </Link>
                    <p className="text-sm text-muted-foreground">
                      Modifié le {format(new Date(article.updated_at || ''), 'dd MMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  <Badge variant={article.is_published ? 'default' : 'secondary'}>
                    {article.is_published ? 'Publié' : 'Brouillon'}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            <Button variant="outline" asChild className="w-full">
              <Link to="/admin/articles">Voir tous les articles</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
